<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <h1 class="mb-2">
                    <span class="text-gradient">DeFi & Trading Analytics</span>
                </h1>
                <p class="text-muted mb-3">DEX volume, token activity, and trading metrics across Solana</p>
                <div class="d-flex gap-3 align-items-center flex-wrap">
                    <date-range-picker default-range="24h" @change="handleDateRangeChange" />
                    <div class="analytics-tabs ms-3">
                        <button :class="['tab-btn', { active: viewMode === 'dex' }]" @click="setViewMode('dex')">
                            By DEX
                        </button>
                        <button :class="['tab-btn', { active: viewMode === 'aggregator' }]" @click="setViewMode('aggregator')">
                            By Aggregator
                        </button>
                        <button :class="['tab-btn', { active: viewMode === 'token' }]" @click="setViewMode('token')">
                            By Token
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Hero Stats -->
        <div class="analytics-hero mb-4">
            <div class="analytics-hero-stat">
                <div class="hero-label">Total Volume</div>
                <div class="hero-value">${{ formatCompact(summary.totalVolume) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">Total Trades</div>
                <div class="hero-value">{{ formatCompact(summary.totalTrades) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue">
                <div class="hero-label">Unique Traders</div>
                <div class="hero-value">{{ formatCompact(summary.totalTraders) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--cyan">
                <div class="hero-label">Active Markets</div>
                <div class="hero-value">{{ formatCompact(summary.totalMarkets) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--amber" v-if="viewMode !== 'token'">
                <div class="hero-label">Aggregated</div>
                <div class="hero-value">{{ summary.aggregatedPercent || 0 }}%</div>
                <div class="hero-subtitle">via Jupiter etc</div>
            </div>
            <div class="analytics-hero-stat hero-stat--amber" v-else>
                <div class="hero-label">Active Tokens</div>
                <div class="hero-value">{{ formatCompact(summary.totalTokens) }}</div>
            </div>
            <div class="analytics-hero-stat">
                <div class="hero-label">DEX Programs</div>
                <div class="hero-value">{{ formatCompact(summary.totalPrograms) }}</div>
            </div>
        </div>

        <!-- SECTION: Leaderboard (moved to top for easy comparison between tabs) -->
        <div class="section-divider">
            <span class="section-title">{{ leaderboardTitle }} by Volume</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Volume Distribution</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="leaderboardChart"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Detailed Breakdown</div>
                </div>
                <div class="panel-body">
                    <div class="table-responsive" style="max-height: 350px;">
                        <table class="compass-table">
                            <thead>
                                <tr>
                                    <th>{{ viewMode === 'token' ? 'Token' : 'Program' }}</th>
                                    <th class="text-center" style="width: 100px;">Trend</th>
                                    <th class="text-end">Volume</th>
                                    <th class="text-end">Trades</th>
                                    <th class="text-end">Traders</th>
                                    <th class="text-end" v-if="viewMode === 'token'">Buy/Sell</th>
                                    <th class="text-end" v-else>Markets</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, idx) in leaderboard" :key="item.program || item.token">
                                    <td>
                                        <span class="rank-badge">{{ idx + 1 }}</span>
                                        <div class="entity-info">
                                            <img v-if="item.logo" :src="item.logo" class="entity-logo" @error="$event.target.style.display='none'" />
                                            <div class="entity-details">
                                                <a v-if="viewMode === 'token'"
                                                   :href="`/tokens/${item.token}`"
                                                   class="text-gradient">
                                                    {{ formatPubkey(item.token) }}
                                                </a>
                                                <a v-else
                                                   :href="`/analytics/programs/${item.program}`"
                                                   class="text-gradient">
                                                    {{ item.name || formatPubkey(item.program) }}
                                                </a>
                                                <span v-if="item.name && viewMode !== 'token'" class="entity-pubkey">
                                                    {{ formatPubkey(item.program) }}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="text-center">
                                        <canvas :ref="el => setSparklineRef(el, item.program || item.token)"
                                                width="80" height="24" class="sparkline"></canvas>
                                    </td>
                                    <td class="text-end font-mono">${{ formatCompact(item.volume) }}</td>
                                    <td class="text-end font-mono">{{ formatCompact(item.trades) }}</td>
                                    <td class="text-end font-mono">{{ formatCompact(item.traders) }}</td>
                                    <td class="text-end" v-if="viewMode === 'token'">
                                        <span :class="getBuySellClass(item.buySellRatio)">
                                            {{ (item.buySellRatio * 100).toFixed(0) }}% buy
                                        </span>
                                    </td>
                                    <td class="text-end font-mono" v-else>{{ item.markets || '-' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Volume Over Time -->
        <div class="section-divider">
            <span class="section-title">Volume Over Time</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Hourly Volume (USD)</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="volumeChart"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Trade Count & Traders</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="tradesChart"></canvas>
                </div>
            </div>
        </div>

        <!-- SECTION: Per-DEX Time Series -->
        <div class="section-divider" v-if="viewMode === 'dex'">
            <span class="section-title">DEX Volume Comparison</span>
        </div>
        <div class="analytics-row cols-1 mb-4" v-if="viewMode === 'dex'">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Top DEX Volume Over Time</div>
                    <div class="panel-subtitle">Stacked area showing volume distribution</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="dexTimeSeriesChart"></canvas>
                </div>
            </div>
        </div>

        <!-- SECTION: Buy vs Sell (Token mode only - DEX index doesn't have buy/sell breakdown) -->
        <div class="section-divider" v-if="viewMode === 'token'">
            <span class="section-title">Buy vs Sell Pressure</span>
        </div>
        <div class="analytics-row cols-1 mb-4" v-if="viewMode === 'token'">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Buy Volume vs Sell Volume</div>
                    <div class="panel-subtitle">Stacked to show directional pressure</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="buySellChart"></canvas>
                </div>
            </div>
        </div>

        <!-- SECTION: Trade Size Distribution -->
        <div class="section-divider">
            <span class="section-title">Trade Size Distribution</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Volume by Trade Size</div>
                    <div class="panel-subtitle">Retail vs Whale activity</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="tradeSizeChart"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Trade Size Breakdown</div>
                </div>
                <div class="panel-body">
                    <div class="size-summary mb-3">
                        <div class="size-stat retail">
                            <div class="size-label">Retail (under $100)</div>
                            <div class="size-value">${{ formatCompact(tradeSizeData.summary?.retailVolume || 0) }}</div>
                            <div class="size-percent">{{ tradeSizeData.summary?.retailPercent || 0 }}%</div>
                        </div>
                        <div class="size-stat whale">
                            <div class="size-label">Whale (over $100)</div>
                            <div class="size-value">${{ formatCompact(tradeSizeData.summary?.whaleVolume || 0) }}</div>
                            <div class="size-percent">{{ tradeSizeData.summary?.whalePercent || 0 }}%</div>
                        </div>
                    </div>
                    <div class="table-responsive" style="max-height: 220px;">
                        <table class="compass-table table-sm">
                            <thead>
                                <tr>
                                    <th>Size Bucket</th>
                                    <th class="text-end">Volume</th>
                                    <th class="text-end">Traders</th>
                                    <th class="text-end">Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(bucket, key) in tradeSizeData.sizeBuckets" :key="key">
                                    <td>{{ bucket.label }}</td>
                                    <td class="text-end font-mono">${{ formatCompact(bucket.volume) }}</td>
                                    <td class="text-end font-mono">{{ formatCompact(bucket.traders) }}</td>
                                    <td class="text-end">
                                        <div class="progress-inline">
                                            <div class="progress-bar" :style="{ width: `${bucket.percent}%` }"></div>
                                            <span>{{ bucket.percent }}%</span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Volume Percentiles -->
        <div class="section-divider">
            <span class="section-title">Trade Size Percentiles</span>
        </div>
        <div class="analytics-row cols-5 mb-4">
            <div class="analytics-panel">
                <div class="panel-body text-center py-3">
                    <div class="percentile-value">${{ formatCompact(volumePercentiles.overall?.p50 || 0) }}</div>
                    <div class="percentile-label">Median (p50)</div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-body text-center py-3">
                    <div class="percentile-value">${{ formatCompact(volumePercentiles.overall?.p75 || 0) }}</div>
                    <div class="percentile-label">p75</div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-body text-center py-3">
                    <div class="percentile-value">${{ formatCompact(volumePercentiles.overall?.p90 || 0) }}</div>
                    <div class="percentile-label">p90</div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-body text-center py-3">
                    <div class="percentile-value">${{ formatCompact(volumePercentiles.overall?.p99 || 0) }}</div>
                    <div class="percentile-label">p99</div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-body text-center py-3">
                    <div class="percentile-value">${{ formatCompact(volumePercentiles.overall?.avgTradeSize || 0) }}</div>
                    <div class="percentile-label">Average</div>
                </div>
            </div>
        </div>

        <!-- SECTION: Aggregator Routing -->
        <div class="section-divider" v-if="viewMode === 'aggregator'">
            <span class="section-title">Aggregator Routing Flow</span>
        </div>
        <div class="analytics-row cols-1 mb-4" v-if="viewMode === 'aggregator'">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">How Aggregators Route to DEXs</div>
                    <div class="panel-subtitle">Total aggregated volume: ${{ formatCompact(aggregatorRouting.totalAggregatedVolume || 0) }}</div>
                </div>
                <div class="panel-body">
                    <div class="routing-flow">
                        <div class="routing-column aggregators">
                            <div class="routing-header">Aggregators</div>
                            <div v-for="node in aggregatorNodes" :key="node.id"
                                 class="routing-node aggregator"
                                 :class="{ active: hoveredAggregator === node.id }"
                                 @mouseenter="hoveredAggregator = node.id"
                                 @mouseleave="hoveredAggregator = null">
                                <span class="node-label">{{ formatPubkey(node.id) }}</span>
                                <span class="node-value">${{ formatCompact(getAggregatorVolume(node.id)) }}</span>
                            </div>
                        </div>
                        <div class="routing-links">
                            <svg width="100%" height="300" preserveAspectRatio="none">
                                <defs>
                                    <linearGradient id="linkGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" style="stop-color:#8b5cf6;stop-opacity:0.6" />
                                        <stop offset="100%" style="stop-color:#10f7cf;stop-opacity:0.6" />
                                    </linearGradient>
                                </defs>
                                <path v-for="(link, idx) in topRoutingLinks" :key="idx"
                                      :d="getLinkPath(link, idx)"
                                      fill="url(#linkGradient)"
                                      :opacity="hoveredAggregator ? (hoveredAggregator === link.source ? 0.8 : 0.1) : 0.4"
                                      class="routing-path" />
                            </svg>
                        </div>
                        <div class="routing-column dexes">
                            <div class="routing-header">DEXs</div>
                            <div v-for="node in dexNodes" :key="node.id" class="routing-node dex">
                                <span class="node-label">{{ formatPubkey(node.id) }}</span>
                                <span class="node-value">${{ formatCompact(getDexVolume(node.id)) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Top Tokens Per DEX -->
        <div class="section-divider" v-if="viewMode === 'dex'">
            <span class="section-title">Top Tokens Per DEX</span>
        </div>
        <div class="analytics-row cols-1 mb-4" v-if="viewMode === 'dex'">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Most Traded Tokens by DEX</div>
                </div>
                <div class="panel-body">
                    <div class="dex-tokens-grid">
                        <div v-for="dex in topTokensPerDex" :key="dex.program" class="dex-token-card">
                            <div class="dex-header">
                                <a :href="`/analytics/programs/${dex.program}`" class="text-gradient">
                                    {{ dex.name || formatPubkey(dex.program) }}
                                </a>
                                <span class="dex-volume">${{ formatCompact(dex.totalVolume) }}</span>
                            </div>
                            <div class="token-list">
                                <div v-for="(token, tIdx) in dex.tokens" :key="token.token" class="token-item">
                                    <span class="token-rank">{{ tIdx + 1 }}</span>
                                    <a :href="`/tokens/${token.token}`" class="token-mint">
                                        {{ formatPubkey(token.token) }}
                                    </a>
                                    <span class="token-volume">${{ formatCompact(token.volume) }}</span>
                                    <span :class="['token-ratio', getBuySellClass(token.buySellRatio)]">
                                        {{ (token.buySellRatio * 100).toFixed(0) }}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Market Activity -->
        <div class="section-divider">
            <span class="section-title">Market Activity Summary</span>
        </div>
        <div class="analytics-row cols-3 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Avg Trade Size</div>
                </div>
                <div class="panel-body text-center py-4">
                    <div class="big-metric">${{ avgTradeSize }}</div>
                    <div class="metric-label">per trade</div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Trades per Trader</div>
                </div>
                <div class="panel-body text-center py-4">
                    <div class="big-metric">{{ tradesPerTrader }}</div>
                    <div class="metric-label">avg trades/trader</div>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Market Concentration</div>
                </div>
                <div class="panel-body text-center py-4">
                    <div class="big-metric">{{ topMarketShare }}%</div>
                    <div class="metric-label">top 3 {{ viewMode === 'token' ? 'tokens' : viewMode === 'aggregator' ? 'aggregators' : 'DEXs' }}</div>
                </div>
            </div>
        </div>

        <!-- SECTION: Time Series Data Table -->
        <div class="section-divider">
            <span class="section-title">Hourly Breakdown</span>
        </div>
        <div class="analytics-row cols-1">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Raw Data</div>
                    <div class="panel-controls">
                        <button class="compass-btn compass-btn--outline compass-btn--sm" @click="exportData">Export CSV</button>
                    </div>
                </div>
                <div class="panel-body">
                    <div class="table-responsive">
                        <table class="compass-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th class="text-end">Volume</th>
                                    <th class="text-end">Trades</th>
                                    <th class="text-end">Traders</th>
                                    <th class="text-end">Buy Vol</th>
                                    <th class="text-end">Sell Vol</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in timeSeries.slice(-24)" :key="row.timestamp">
                                    <td>{{ row.label }}</td>
                                    <td class="text-end font-mono">${{ formatCompact(row.volume) }}</td>
                                    <td class="text-end font-mono">{{ formatCompact(row.trades) }}</td>
                                    <td class="text-end font-mono">{{ formatCompact(row.traders) }}</td>
                                    <td class="text-end font-mono text-success">${{ formatCompact(row.buyVolume) }}</td>
                                    <td class="text-end font-mono text-danger">${{ formatCompact(row.sellVolume) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { colors, getBarChartOptions, getLineChartOptions, getStackedBarChartOptions, getDoughnutChartOptions } from '../composables/useChartConfig';

export default {
    name: 'DefiAnalytics',

    data() {
        return {
            currentRange: '24h',
            viewMode: 'dex', // 'dex', 'aggregator', or 'token'
            summary: {},
            timeSeries: [],
            leaderboard: [],
            tradeSizeData: { sizeBuckets: {}, summary: {} },
            volumePercentiles: { overall: {}, byDex: [] },
            aggregatorRouting: { nodes: [], links: [], totalAggregatedVolume: 0 },
            topTokensPerDex: [],
            dexTimeSeries: { dexes: [], series: {} },
            sparklineRefs: {},
            hoveredAggregator: null,
            refreshInterval: null
        };
    },

    created() {
        this.charts = {};
    },

    computed: {
        leaderboardTitle() {
            if (this.viewMode === 'token') return 'Top Tokens';
            if (this.viewMode === 'aggregator') return 'Top Aggregators';
            return 'Top DEX Programs';
        },
        avgTradeSize() {
            if (!this.summary.totalTrades) return '0';
            return this.formatCompact(this.summary.totalVolume / this.summary.totalTrades);
        },
        tradesPerTrader() {
            if (!this.summary.totalTraders) return '0';
            return (this.summary.totalTrades / this.summary.totalTraders).toFixed(1);
        },
        topMarketShare() {
            if (!this.leaderboard.length || !this.summary.totalVolume) return '0';
            const top3Vol = this.leaderboard.slice(0, 3).reduce((sum, item) => sum + item.volume, 0);
            return ((top3Vol / this.summary.totalVolume) * 100).toFixed(1);
        },
        aggregatorNodes() {
            return (this.aggregatorRouting.nodes || []).filter(n => n.type === 'aggregator');
        },
        dexNodes() {
            return (this.aggregatorRouting.nodes || []).filter(n => n.type === 'dex');
        },
        topRoutingLinks() {
            return (this.aggregatorRouting.links || []).slice(0, 20);
        }
    },

    mounted() {
        this.initCharts();
        this.loadData();
        this.refreshInterval = setInterval(() => this.loadData(), 60000);
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
        if (this.refreshInterval) clearInterval(this.refreshInterval);
    },

    methods: {
        handleDateRangeChange(data) {
            this.currentRange = data.range;
            this.loadData();
        },

        setViewMode(val) {
            this.viewMode = val;
            this.loadData();
        },

        setSparklineRef(el, id) {
            if (el) this.sparklineRefs[id] = el;
        },

        initCharts() {
            // Use design system chart configurations
            this.charts.volume = new Chart(this.$refs.volumeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({ formatCallback: v => '$' + this.formatCompact(v) })
            });

            this.charts.trades = new Chart(this.$refs.tradesChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...getLineChartOptions({ formatCallback: v => this.formatCompact(v) }),
                    scales: {
                        y1: { type: 'linear', position: 'left', ticks: { callback: v => this.formatCompact(v) }, min: 0 },
                        y2: { type: 'linear', position: 'right', ticks: { callback: v => this.formatCompact(v) }, grid: { drawOnChartArea: false }, min: 0 }
                    }
                }
            });

            this.charts.buySell = new Chart(this.$refs.buySellChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getStackedBarChartOptions({ formatCallback: v => '$' + this.formatCompact(v) })
            });

            this.charts.leaderboard = new Chart(this.$refs.leaderboardChart, {
                type: 'doughnut',
                data: { labels: [], datasets: [] },
                options: {
                    ...getDoughnutChartOptions({ legendPosition: 'right' }),
                    plugins: {
                        legend: {
                            position: 'right',
                            labels: {
                                generateLabels: chart => {
                                    const data = chart.data;
                                    return data.labels.slice(0, 8).map((label, i) => ({
                                        text: this.formatPubkey(label),
                                        fillStyle: data.datasets[0].backgroundColor[i],
                                        hidden: false,
                                        index: i
                                    }));
                                }
                            }
                        },
                        tooltip: {
                            callbacks: {
                                label: ctx => {
                                    const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                                    const pct = ((ctx.parsed / total) * 100).toFixed(1);
                                    return `$${this.formatCompact(ctx.parsed)} (${pct}%)`;
                                }
                            }
                        }
                    }
                }
            });

            this.charts.tradeSize = new Chart(this.$refs.tradeSizeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({ horizontal: true, formatCallback: v => '$' + this.formatCompact(v), showLegend: false })
            });

            // DEX time series chart (stacked area)
            if (this.$refs.dexTimeSeriesChart) {
                this.charts.dexTimeSeries = new Chart(this.$refs.dexTimeSeriesChart, {
                    type: 'line',
                    data: { labels: [], datasets: [] },
                    options: getLineChartOptions({ formatCallback: v => '$' + this.formatCompact(v), fill: true })
                });
            }
        },

        async loadData() {
            try {
                // Load main data
                if (this.viewMode === 'token') {
                    const res = await fetch(`/analytics/api/token-volume-metrics?range=${this.currentRange}&limit=20`);
                    const data = await res.json();
                    this.summary = data.summary || {};
                    this.timeSeries = data.timeSeries || [];
                    this.leaderboard = data.tokens || [];
                } else {
                    const res = await fetch(`/analytics/api/defi-metrics?range=${this.currentRange}&limit=20`);
                    const data = await res.json();
                    this.summary = data.summary || {};
                    this.timeSeries = data.timeSeries || [];
                    if (this.viewMode === 'aggregator') {
                        this.leaderboard = data.aggregators || [];
                    } else {
                        this.leaderboard = data.dexPrograms || [];
                    }
                }

                // Load additional data in parallel
                const additionalLoads = [
                    this.loadTradeSizeDistribution(),
                    this.loadVolumePercentiles()
                ];

                if (this.viewMode === 'aggregator') {
                    additionalLoads.push(this.loadAggregatorRouting());
                }

                if (this.viewMode === 'dex') {
                    additionalLoads.push(this.loadTopTokensPerDex());
                    additionalLoads.push(this.loadDexTimeSeries());
                }

                await Promise.all(additionalLoads);

                this.updateCharts();
                this.$nextTick(() => this.updateSparklines());

            } catch (e) {
                console.error('Error loading DeFi data:', e);
            }
        },

        async loadTradeSizeDistribution() {
            try {
                const res = await fetch(`/analytics/api/trade-size-distribution?range=${this.currentRange}`);
                this.tradeSizeData = await res.json();
            } catch (e) {
                console.error('Error loading trade size distribution:', e);
            }
        },

        async loadVolumePercentiles() {
            try {
                const res = await fetch(`/analytics/api/volume-percentiles?range=${this.currentRange}`);
                this.volumePercentiles = await res.json();
            } catch (e) {
                console.error('Error loading volume percentiles:', e);
            }
        },

        async loadAggregatorRouting() {
            try {
                const res = await fetch(`/analytics/api/aggregator-routing?range=${this.currentRange}`);
                this.aggregatorRouting = await res.json();
            } catch (e) {
                console.error('Error loading aggregator routing:', e);
            }
        },

        async loadTopTokensPerDex() {
            try {
                const res = await fetch(`/analytics/api/top-tokens-per-dex?range=${this.currentRange}&dex_limit=6&token_limit=5`);
                this.topTokensPerDex = await res.json();
            } catch (e) {
                console.error('Error loading top tokens per DEX:', e);
            }
        },

        async loadDexTimeSeries() {
            try {
                const res = await fetch(`/analytics/api/dex-time-series?range=${this.currentRange}&limit=5`);
                this.dexTimeSeries = await res.json();
            } catch (e) {
                console.error('Error loading DEX time series:', e);
            }
        },

        updateCharts() {
            const ts = this.timeSeries;
            const labels = ts.map(d => d.label);

            // Volume chart
            this.charts.volume.data = {
                labels,
                datasets: [{
                    label: 'Volume (USD)',
                    data: ts.map(d => d.volume),
                    backgroundColor: 'rgba(16, 247, 207, 0.6)',
                    borderColor: 'rgb(16, 247, 207)',
                    borderWidth: 1
                }]
            };
            this.charts.volume.update();

            // Trades chart
            this.charts.trades.data = {
                labels,
                datasets: [
                    { label: 'Trades', data: ts.map(d => d.trades), borderColor: '#8b5cf6', yAxisID: 'y1' },
                    { label: 'Traders', data: ts.map(d => d.traders), borderColor: '#ec4899', yAxisID: 'y2' }
                ]
            };
            this.charts.trades.update();

            // Buy/Sell chart
            this.charts.buySell.data = {
                labels,
                datasets: [
                    { label: 'Buy Volume', data: ts.map(d => d.buyVolume || 0), backgroundColor: 'rgba(34, 197, 94, 0.7)' },
                    { label: 'Sell Volume', data: ts.map(d => d.sellVolume || 0), backgroundColor: 'rgba(239, 68, 68, 0.7)' }
                ]
            };
            this.charts.buySell.update();

            // Leaderboard chart
            const top8 = this.leaderboard.slice(0, 8);
            const colors = ['#8b5cf6', '#ec4899', '#10f7cf', '#f59e0b', '#3b82f6', '#22c55e', '#ef4444', '#a855f7'];
            this.charts.leaderboard.data = {
                labels: top8.map(d => d.token || d.program),
                datasets: [{
                    data: top8.map(d => d.volume),
                    backgroundColor: colors.slice(0, top8.length)
                }]
            };
            this.charts.leaderboard.update();

            // Trade size chart
            const sizeBuckets = this.tradeSizeData.sizeBuckets || {};
            const sizeLabels = Object.values(sizeBuckets).map(b => b.label);
            const sizeVolumes = Object.values(sizeBuckets).map(b => b.volume);
            const sizeColors = ['#3b82f6', '#22c55e', '#f59e0b', '#ec4899', '#8b5cf6'];

            this.charts.tradeSize.data = {
                labels: sizeLabels,
                datasets: [{
                    data: sizeVolumes,
                    backgroundColor: sizeColors.slice(0, sizeLabels.length),
                    borderRadius: 4
                }]
            };
            this.charts.tradeSize.update();

            // DEX time series chart (if in DEX mode)
            if (this.charts.dexTimeSeries && this.viewMode === 'dex') {
                const dexes = this.dexTimeSeries.dexes || [];
                const series = this.dexTimeSeries.series || {};

                if (dexes.length > 0) {
                    const firstDex = series[dexes[0]] || [];
                    const dexLabels = firstDex.map(d => d.label);

                    const datasets = dexes.map((dex, i) => ({
                        label: this.formatPubkey(dex),
                        data: (series[dex] || []).map(d => d.volume),
                        borderColor: colors[i % colors.length],
                        backgroundColor: colors[i % colors.length] + '40',
                        fill: true
                    }));

                    this.charts.dexTimeSeries.data = { labels: dexLabels, datasets };
                    this.charts.dexTimeSeries.update();
                }
            }
        },

        updateSparklines() {
            const colors = ['#8b5cf6', '#ec4899', '#10f7cf', '#f59e0b', '#3b82f6'];

            this.leaderboard.forEach((item, idx) => {
                const id = item.program || item.token;
                const canvas = this.sparklineRefs[id];
                if (!canvas) return;

                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, 80, 24);

                // Generate mock sparkline data (in real implementation, use historical data)
                const dataPoints = 12;
                const data = [];
                let val = item.volume / dataPoints;
                for (let i = 0; i < dataPoints; i++) {
                    val = val * (0.8 + Math.random() * 0.4);
                    data.push(val);
                }

                const max = Math.max(...data);
                const min = Math.min(...data);
                const range = max - min || 1;

                ctx.beginPath();
                ctx.strokeStyle = colors[idx % colors.length];
                ctx.lineWidth = 1.5;

                data.forEach((d, i) => {
                    const x = (i / (dataPoints - 1)) * 76 + 2;
                    const y = 22 - ((d - min) / range) * 18;
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                });

                ctx.stroke();
            });
        },

        getAggregatorVolume(aggregatorId) {
            const links = this.aggregatorRouting.links || [];
            return links.filter(l => l.source === aggregatorId).reduce((sum, l) => sum + l.value, 0);
        },

        getDexVolume(dexId) {
            const links = this.aggregatorRouting.links || [];
            return links.filter(l => l.target === dexId).reduce((sum, l) => sum + l.value, 0);
        },

        getLinkPath(link, idx) {
            const aggNodes = this.aggregatorNodes;
            const dexNodes = this.dexNodes;

            const sourceIdx = aggNodes.findIndex(n => n.id === link.source);
            const targetIdx = dexNodes.findIndex(n => n.id === link.target);

            if (sourceIdx === -1 || targetIdx === -1) return '';

            const y1 = 30 + sourceIdx * 50;
            const y2 = 30 + targetIdx * 50;
            const thickness = Math.min(Math.max(link.value / 1e8, 2), 20);

            return `M 0,${y1} C 100,${y1} 100,${y2} 200,${y2} L 200,${y2 + thickness} C 100,${y2 + thickness} 100,${y1 + thickness} 0,${y1 + thickness} Z`;
        },

        exportData() {
            const csv = [['Time', 'Volume', 'Trades', 'Traders', 'Buy Volume', 'Sell Volume'].join(',')];
            this.timeSeries.forEach(r => {
                csv.push([r.label, r.volume, r.trades, r.traders, r.buyVolume || 0, r.sellVolume || 0].join(','));
            });
            const blob = new Blob([csv.join('\n')], { type: 'text/csv' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `defi_${this.currentRange}_${this.viewMode}.csv`;
            a.click();
        },

        getBuySellClass(ratio) {
            if (ratio > 0.6) return 'text-success';
            if (ratio < 0.4) return 'text-danger';
            return 'text-muted';
        },

        formatPubkey(a) {
            return a?.length > 12 ? `${a.slice(0, 4)}..${a.slice(-4)}` : a;
        },

        formatCompact(v) {
            if (!v) return '0';
            if (v >= 1e12) return (v / 1e12).toFixed(2) + 'T';
            if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B';
            if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M';
            if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
            return v.toLocaleString();
        }
    }
};
</script>

<style scoped>
/* Minimal component-specific styles - all common styles now in _compass-analytics.scss */

/* Routing node variants specific to this component */
.routing-node.aggregator {
    background: rgba(139, 92, 246, 0.15);
    border: 1px solid rgba(139, 92, 246, 0.3);
}

.routing-node.aggregator.active {
    background: rgba(139, 92, 246, 0.3);
    border-color: #8b5cf6;
}

.routing-node.dex {
    background: rgba(16, 247, 207, 0.1);
    border: 1px solid rgba(16, 247, 207, 0.3);
}

.node-label {
    font-size: 0.8rem;
    font-family: 'JetBrains Mono', monospace;
}

.node-value {
    font-size: 0.75rem;
    color: #6b7280;
}

.dark-mode .node-value {
    color: rgba(255,255,255,0.6);
}

.routing-path {
    transition: opacity 0.2s;
}

/* DEX Tokens Grid - specific to this component */
.dex-tokens-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
}

.dex-token-card {
    background: rgba(139, 92, 246, 0.05);
    border: 1px solid rgba(139, 92, 246, 0.15);
    border-radius: 8px;
    padding: 0.75rem;
}

.dex-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(0,0,0,0.1);
}

.dark-mode .dex-header {
    border-color: rgba(255,255,255,0.1);
}

.dex-volume {
    font-size: 0.85rem;
    font-family: 'JetBrains Mono', monospace;
    color: #6b7280;
}

.dark-mode .dex-volume {
    color: rgba(255,255,255,0.6);
}

.token-list {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}

.token-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
}

.token-rank {
    width: 18px;
    height: 18px;
    line-height: 18px;
    text-align: center;
    border-radius: 50%;
    background: rgba(139, 92, 246, 0.1);
    font-size: 0.65rem;
    color: #6b7280;
}

.dark-mode .token-rank {
    background: rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.6);
}

.token-mint {
    flex: 1;
    font-family: 'JetBrains Mono', monospace;
    color: #374151;
    text-decoration: none;
}

.dark-mode .token-mint {
    color: rgba(255,255,255,0.8);
}

.token-mint:hover {
    color: #10f7cf;
}

.token-volume {
    font-family: 'JetBrains Mono', monospace;
    color: #6b7280;
}

.dark-mode .token-volume {
    color: rgba(255,255,255,0.6);
}

.token-ratio {
    font-size: 0.7rem;
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
    background: rgba(139, 92, 246, 0.1);
}

.dark-mode .token-ratio {
    background: rgba(255,255,255,0.1);
}

/* Sparkline */
.sparkline {
    display: block;
}

/* Table small variant */
.table-sm td, .table-sm th {
    padding: 0.35rem 0.5rem;
    font-size: 0.85rem;
}

/* Entity info (DEX/program with name and logo) */
.entity-info {
    display: flex;
    align-items: center;
    gap: 8px;
}

.entity-logo {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
}

.entity-details {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.entity-pubkey {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.65rem;
    color: var(--compass-text-secondary, #7d8590);
    opacity: 0.7;
}
</style>
